import { api } from "@/utils/api";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import { useMemo, useState } from "react";

export type ActivityStatisticsCampaign = {
  id: number;
  averageBurnRate: number;
  received: number;
  receivedAndHandled: number;
  success: number;
  disqualified: number;
  postponed: number;
  successRate: number;
  burned: number;
};

export type ActivityStatisticsUser = {
  id: string;
  averageBurnRate: number;
  received: number;
  receivedAndHandled: number;
  success: number;
  disqualified: number;
  postponed: number;
  successRate: number;
  burned: number;
};

export type ActivityStatisticsTotal = {
  averageBurnRate: number;
  received: number;
  receivedAndHandled: number;
  success: number;
  disqualified: number;
  postponed: number;
  successRate: number;
  burned: number;
};

export type ActivityStatisticsPeriod = {
  start: string;
  end: string;
  received: number;
  receivedAndHandled: number;
  success: number;
  disqualified: number;
  postponed: number;
  successRate: number;
  burned: number;
};

export type ActivityStatisticsResponse = {
  campaigns: ActivityStatisticsCampaign[];
  users: ActivityStatisticsUser[];
  total: ActivityStatisticsTotal;
  periods: ActivityStatisticsPeriod[];
};

export type ActivityStatisticsRequest = {
  campaignIds: number[];
  userIds: string[];
};

export type ActivityStatisticsParams = {
  period: string;
  from: string;
  to: string | number;
  campaignIds: number[];
  userIds: string[];
};

export const activitiesStatisticsApi = {
  getStatistics: async ({
    period,
    from,
    to,
    campaignIds,
    userIds,
  }: ActivityStatisticsParams): Promise<ActivityStatisticsResponse> => {
    const searchParams = {
      from,
      to,
    };

    const requestBody: ActivityStatisticsRequest = {
      campaignIds,
      userIds,
    };

    return await api
      .post(
        `apps/sales/activities/statistics/${period}?from=${searchParams.from}&to=${searchParams.to}`,
        {
          json: requestBody,
        },
      )
      .json<ActivityStatisticsResponse>();
  },
};

export const useActivitiesStatistics = (
  params: ActivityStatisticsParams,
  options?: {
    enabled?: boolean;
    refetchInterval?: number;
  },
) => {
  const { enabled = true, refetchInterval } = options || {};

  // Sort arrays where order doesn't matter to avoid unnecessary refetches.
  params = useMemo(
    () => ({
      ...params,
      campaignIds: [...params.campaignIds].sort(),
      userIds: [...params.userIds].sort(),
    }),
    [params],
  );

  const debouncedParams = useDebounce(params, 800);

  const paramsJSON = useMemo(() => JSON.stringify(params), [params]);
  const debouncedParamsJSON = useMemo(
    () => JSON.stringify(debouncedParams),
    [debouncedParams],
  );

  const isWaitingForNewParams = useMemo(
    () => paramsJSON !== debouncedParamsJSON,
    [paramsJSON, debouncedParamsJSON],
  );

  // Track the params used in the latest successful query.
  const [latestQueryDataParams, setLatestQueryDataParams] =
    useState<ActivityStatisticsParams>();
  const latestQueryDataParamsJSON = useMemo(
    () =>
      latestQueryDataParams ? JSON.stringify(latestQueryDataParams) : null,
    [latestQueryDataParams],
  );

  // Determine if the current params differ from those of the latest successful query.
  const hasStaleParams = useMemo(
    () => paramsJSON !== latestQueryDataParamsJSON,
    [paramsJSON, latestQueryDataParamsJSON],
  );

  const query = useQuery({
    queryKey: [
      "activitiesStatistics",
      debouncedParams.period,
      debouncedParams.from,
      debouncedParams.to,
      debouncedParams.campaignIds,
      debouncedParams.userIds,
    ],
    queryFn: async () => {
      const result =
        await activitiesStatisticsApi.getStatistics(debouncedParams);
      setLatestQueryDataParams(debouncedParams);
      return result;
    },
    enabled:
      enabled &&
      !!debouncedParams.period &&
      !!debouncedParams.from &&
      !!debouncedParams.to,
    refetchInterval,
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData,
    gcTime: 10 * 60 * 1000,
  });

  return { ...query, isWaitingForNewParams, hasStaleParams };
};

export const useActivitiesStatisticsWithRefresh = (
  params: ActivityStatisticsParams,
  refreshInterval = 30000,
) => {
  const queryResult = useActivitiesStatistics(params, {
    refetchInterval: refreshInterval,
  });

  return {
    ...queryResult,
    isUpdating: queryResult.isFetching && queryResult.isPlaceholderData,
    hasData: !!queryResult.data,
  };
};
